import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { BossBabyToolDto } from '@/dto/BossBabyDto';

interface ToolCardProps {
  tool: BossBabyToolDto;
  onEdit: (toolId: number) => void;
  onDelete: (toolId: number) => void;
}

export const ToolCard: React.FC<ToolCardProps> = React.memo(({ 
  tool, 
  onEdit, 
  onDelete 
}) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon color="primary" />
            <Typography variant="h6" component="span">
              {tool.toolName}
            </Typography>
          </Box>
        }
        action={
          <Box>
            <Tooltip title="Edit Tool">
              <IconButton 
                size="small" 
                onClick={() => onEdit(tool.toolId)}
                color="primary"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Tool">
              <IconButton 
                size="small" 
                onClick={() => onDelete(tool.toolId)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
        sx={{ pb: 1 }}
      />
      
      <CardContent sx={{ flexGrow: 1, pt: 0 }}>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2, 
            minHeight: '40px',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {tool.description || 'No description provided'}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip 
            label={tool.category} 
            size="small" 
            variant="outlined"
            color="secondary"
          />
          <Chip 
            label={tool.priorityLevel} 
            size="small" 
            color={getPriorityColor(tool.priorityLevel) as any}
          />
          <Chip 
            label={tool.isActive ? 'Active' : 'Inactive'} 
            size="small" 
            color={tool.isActive ? 'success' : 'error'}
            variant={tool.isActive ? 'filled' : 'outlined'}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ScheduleIcon fontSize="small" color="disabled" />
          <Typography variant="caption" color="text.secondary">
            Timeout: {tool.timeoutSeconds}s
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block">
          Created: {formatDate(tool.createdAt)}
        </Typography>
        
        {tool.updatedAt !== tool.createdAt && (
          <Typography variant="caption" color="text.secondary" display="block">
            Updated: {formatDate(tool.updatedAt)}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
});